/** Project queries and mutations. All server state flows through TanStack Query. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'
import type { Project } from '@/lib/domain'
import { queryKeys } from '@/lib/query'

export interface ProjectFilters {
  stage?: string
  status?: string
  workstream?: string
  team?: string
  categoryId?: string
  /** One initiative's projects. */
  initiativeId?: string
  ragStatus?: string
  /** Exact owner name, from the list of owners that actually exist. */
  owner?: string
  /** Past target and not finished. Sent as `overdue=true`. */
  overdue?: string
  /** `'true'` returns the archive — complete and cancelled — instead of live work. */
  archived?: string
  search?: string
}

/** Drop empty filters so the query key is stable and the URL stays clean. */
const clean = (filters: ProjectFilters): Record<string, string> =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value && value !== 'all'),
  ) as Record<string, string>

export function useProjects(filters: ProjectFilters) {
  const params = clean(filters)
  return useQuery({
    queryKey: queryKeys.projects(params),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects', { params: { query: params } })
      return data as unknown as { projects: Project[]; total: number }
    },
  })
}

/** The stack rank — the whole portfolio, or one team's when `team` is given. */
export interface RankedFilters {
  team?: string
  categoryId?: string
}

export function useRankedProjects(filters: RankedFilters = {}) {
  const params = clean(filters)
  return useQuery({
    queryKey: queryKeys.ranked(params),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/ranked', { params: { query: params } })
      return data as unknown as { projects: Project[] }
    },
  })
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.project(id ?? ''),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{project_id}', {
        params: { path: { project_id: id as string } },
      })
      return data as unknown as Project
    },
  })
}

/**
 * Invalidate every view a project write can change, so no screen shows stale data.
 *
 * "Every view" has to mean every view. Listing only `projects` here is what made a new
 * project take an F5 to show up in the team bar: the counts sat on the client's 30-second
 * `staleTime` and waited for a remount or a page reload. Pages now also poll each minute,
 * but a minute is a long time to look at your own change not having happened.
 * Invalidating refetches immediately whatever the staleTime says, which is the whole point
 * of doing it here.
 *
 * The server was never the problem. It answers in about 300ms and reflects a new project
 * at once.
 */
function useInvalidateProjects() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['projects'] })
    // The team bar counts projects: create and delete move a total, and an edit can move a
    // project from one team to another.
    void client.invalidateQueries({ queryKey: ['teams'] })
    // Roadmap and status report are built from projects — a new project with a target date
    // belongs on the roadmap now, and a health or stage change belongs in the report now.
    void client.invalidateQueries({ queryKey: ['reports'] })
  }
}

export function useCreateProject() {
  const invalidate = useInvalidateProjects()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await api.POST('/api/v1/projects', { body: body as never })
      return data as unknown as Project
    },
    onSuccess: invalidate,
  })
}

export function useUpdateProject(id: string) {
  const invalidate = useInvalidateProjects()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await api.PATCH('/api/v1/projects/{project_id}', {
        params: { path: { project_id: id } },
        body: body as never,
      })
      return data as unknown as Project
    },
    onSuccess: invalidate,
  })
}

/** Move a project through a phase gate. The API requires a reason. */
export function useTransitionStage(id: string) {
  const invalidate = useInvalidateProjects()
  return useMutation({
    mutationFn: async (body: { target_stage: string; reason: string; row_version: number }) => {
      const { data } = await api.POST('/api/v1/projects/{project_id}/stage', {
        params: { path: { project_id: id } },
        body: body as never,
      })
      return data as unknown as Project
    },
    onSuccess: invalidate,
  })
}

/** Put a project on hold, resume it, or cancel it. Portfolio Managers and Administrators. */
export function useSetProjectStatus(id: string) {
  const invalidate = useInvalidateProjects()
  return useMutation({
    mutationFn: async (body: {
      status: string
      reason: string
      on_hold_until: string | null
      row_version: number
    }) => {
      const { data } = await api.POST('/api/v1/projects/{project_id}/status', {
        params: { path: { project_id: id } },
        body: body as never,
      })
      return data as unknown as Project
    },
    onSuccess: invalidate,
  })
}

/** Soft-delete a project; its work items go to the backlog or with it. */
export function useDeleteProject(id: string) {
  const invalidate = useInvalidateProjects()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: { reason: string; work_items: 'backlog' | 'delete' }) => {
      await api.DELETE('/api/v1/projects/{project_id}', {
        params: { path: { project_id: id } },
        body: body as never,
      })
    },
    onSuccess: () => {
      invalidate()
      // The items moved off the project, or went with it — both change the boards.
      void client.invalidateQueries({ queryKey: ['work-items'] })
      void client.invalidateQueries({ queryKey: ['sprints'] })
    },
  })
}

export function useCloseProject(id: string) {
  const invalidate = useInvalidateProjects()
  return useMutation({
    mutationFn: async (body: {
      summary: string
      handoff_note: string
      row_version: number
    }) => {
      const { data } = await api.POST('/api/v1/projects/{project_id}/closure', {
        params: { path: { project_id: id } },
        body: body as never,
      })
      return data as unknown as Project
    },
    onSuccess: invalidate,
  })
}

/**
 * Correct a closure record after the fact.
 *
 * `useCloseProject` refuses a project that is already complete, which is right. This is
 * for fixing the record — the projects migrated from the pilot arrived with a summary and
 * no handoff note, and there was no route to add one.
 */
export function useAmendClosure(id: string) {
  const invalidate = useInvalidateProjects()
  return useMutation({
    mutationFn: async (body: {
      summary?: string
      handoff_note?: string
      reason: string
      row_version: number
    }) => {
      const { data } = await api.PATCH('/api/v1/projects/{project_id}/closure', {
        params: { path: { project_id: id } },
        body: body as never,
      })
      return data as unknown as Project
    },
    onSuccess: invalidate,
  })
}

export function useScoreProject(id: string) {
  const invalidate = useInvalidateProjects()
  return useMutation({
    mutationFn: async (body: {
      value: number | null
      risk: number | null
      effort: number | null
      row_version: number
    }) => {
      const { data } = await api.PUT('/api/v1/projects/{project_id}/scores', {
        params: { path: { project_id: id } },
        body: body as never,
      })
      return data as unknown as Project
    },
    onSuccess: invalidate,
  })
}

/**
 * Pin a project to a position in the stack rank, or release it.
 *
 * Pass `position: null` to release. The API insists on a reason when pinning, because a pin
 * overrides the score everyone else's work was ordered by.
 */
export function usePinProject() {
  const invalidate = useInvalidateProjects()
  return useMutation({
    mutationFn: async ({
      id,
      position,
      reason,
      rowVersion,
    }: {
      id: string
      position: number | null
      reason: string
      rowVersion: number
    }) => {
      const { data } = await api.PUT('/api/v1/projects/{project_id}/rank', {
        params: { path: { project_id: id } },
        body: { position, reason, row_version: rowVersion } as never,
      })
      return data as unknown as Project
    },
    onSuccess: invalidate,
  })
}

/**
 * Set percent complete by hand, or hand it back to the rollup.
 *
 * `percent: null` releases the override; the server recomputes immediately.
 */
export function useSetPercentComplete() {
  const invalidate = useInvalidateProjects()
  return useMutation({
    mutationFn: async ({
      id,
      percent,
      rowVersion,
    }: {
      id: string
      percent: number | null
      rowVersion: number
    }) => {
      const { data } = await api.PUT('/api/v1/projects/{project_id}/percent-complete', {
        params: { path: { project_id: id } },
        body: { percent, row_version: rowVersion } as never,
      })
      return data as unknown as Project
    },
    onSuccess: invalidate,
  })
}

export interface DateChange {
  id: string
  field: 'start_date' | 'target_date'
  previousDate: string | null
  newDate: string | null
  daysMoved: number | null
  reason: string
  changedAt: string
  changedBy: string
}

/** How this project's dates have moved, oldest first. */
export function useDateChanges(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'date-changes'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{project_id}/date-changes', {
        params: { path: { project_id: projectId } },
      })
      return data as unknown as { changes: DateChange[] }
    },
  })
}

export interface StageMove {
  stage: string
  previousStage: string | null
  reason: string
  occurredAt: string
  actor: string
  /** True when the rollup moved it, not a person. */
  automatic: boolean
}

/** Which stages this project has been in, newest first. */
export function useStageHistory(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'stage-history'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{project_id}/stage-history', {
        params: { path: { project_id: projectId } },
      })
      return data as unknown as { history: StageMove[] }
    },
  })
}
